import React, { useEffect, useRef, useState } from 'react';
import { Send, Image as ImgIcon, Mic, Square, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * WhatsApp-style chat view. Shared by student (public) and teacher (staff) sides.
 *
 * Props:
 * - chatId, wsUrl (already built with token & role)
 * - identity: 'student' | 'teacher'  (controls message alignment/colors)
 * - initialMessages: array
 * - onSendText(content)   -> Promise<message>
 * - onSendMedia(file, mediaType)  -> Promise<message>
 * - onRefetch()  -> refetch messages (fallback if WS drops)
 * - header: {title, subtitle}
 */
export default function DoubtChatView({
  chatId, wsUrl, identity, initialMessages = [],
  onSendText, onSendMedia, onRefetch, header, hideVoice = false, disabled = false,
  onClosed,
}) {
  const [messages, setMessages] = useState(initialMessages);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const wsRetryRef = useRef(0);

  // Keep messages in sync when parent refetches (e.g., switching chats)
  useEffect(() => { setMessages(initialMessages || []); }, [chatId, initialMessages]);

  // Scroll to bottom on new message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket connect + auto-reconnect
  useEffect(() => {
    if (!chatId || !wsUrl) return;
    let alive = true;

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (data.type === 'message' && data.message) {
              setMessages((prev) => {
                if (prev.some((m) => m.id === data.message.id)) return prev;
                return [...prev, data.message];
              });
            } else if (data.type === 'closed') {
              if (data.message) {
                setMessages((prev) => {
                  if (prev.some((m) => m.id === data.message.id)) return prev;
                  return [...prev, data.message];
                });
              }
              if (typeof onClosed === 'function') {
                try { onClosed(data.chat || null); } catch (_) { /* ignore */ }
              }
            }
          } catch (e) { /* ignore non-JSON */ }
        };

        ws.onclose = () => {
          if (!alive) return;
          wsRetryRef.current += 1;
          const delay = Math.min(1000 * (2 ** wsRetryRef.current), 15000);
          setTimeout(() => alive && connect(), delay);
        };

        ws.onopen = () => { wsRetryRef.current = 0; };

        ws.onerror = () => { /* onclose will trigger reconnect */ };
      } catch (e) {
        console.warn('WS connect failed', e);
      }
    };

    connect();
    return () => {
      alive = false;
      try { wsRef.current?.close(); } catch (e) { /* noop */ }
    };
  }, [chatId, wsUrl]);

  const handleSendText = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const msg = await onSendText(content);
      setText('');
      // If WS is delayed, optimistically add
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large (max 25MB)'); return; }
    setUploading(true);
    try {
      const msg = await onSendMedia(file, 'image');
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Image upload failed');
    } finally { setUploading(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (evt) => { if (evt.data.size > 0) audioChunksRef.current.push(evt.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mime || 'audio/webm' });
        if (blob.size < 500) { toast.error('Recording too short'); return; }
        const ext = (mime || 'audio/webm').includes('webm') ? 'webm' : 'ogg';
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: mime || 'audio/webm' });
        setUploading(true);
        try {
          const msg = await onSendMedia(file, 'audio');
          setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Voice upload failed');
        } finally { setUploading(false); }
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-[#0f172a] to-[#020617] text-slate-100" data-testid="doubt-chat-view">
      {/* Header */}
      {header && (
        <div className="px-4 py-3 border-b border-slate-700/60 bg-slate-900/80 backdrop-blur flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-300 font-bold">
            {(header.title || '?')[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate">{header.title}</div>
            {header.subtitle && <div className="text-xs text-slate-400 truncate">{header.subtitle}</div>}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2" style={{ backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(16,185,129,0.06), transparent 40%), radial-gradient(circle at 80% 90%, rgba(59,130,246,0.05), transparent 40%)' }}>
        {messages.length === 0 && (
          <div className="text-center text-slate-500 mt-16 text-sm">No messages yet. Say hi 👋</div>
        )}
        {messages.map((m) => {
          if (m.sender_type === 'system' || m.media_type === 'system') {
            return (
              <div key={m.id} data-testid={`doubt-msg-${m.id}`} className="flex justify-center">
                <div className="max-w-[80%] text-center text-[11px] px-3 py-1 rounded-full bg-slate-800/70 border border-slate-700/60 text-slate-300">
                  {m.content}
                </div>
              </div>
            );
          }
          const mine = m.sender_type === identity;
          return (
            <div key={m.id} data-testid={`doubt-msg-${m.id}`}
                 className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 shadow-md ${
                mine
                  ? 'bg-emerald-600/90 text-white rounded-br-sm'
                  : 'bg-slate-800/90 text-slate-100 rounded-bl-sm border border-slate-700/70'
              }`}>
                {!mine && (
                  <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${mine ? 'text-emerald-100' : 'text-emerald-300'}`}>
                    {m.sender_name}
                  </div>
                )}
                {m.media_type === 'image' && m.media_url && (
                  <img src={m.media_url} alt="attached"
                       onClick={() => setPreviewImg(m.media_url)}
                       className="rounded-lg max-h-64 object-cover mb-1 cursor-zoom-in" />
                )}
                {m.media_type === 'audio' && m.media_url && (
                  <audio controls src={m.media_url} className="w-56 max-w-full" />
                )}
                {m.content && <div className="whitespace-pre-wrap break-words text-sm">{m.content}</div>}
                <div className={`text-[10px] mt-1 ${mine ? 'text-emerald-100/70' : 'text-slate-400'} text-right`}>
                  {formatTime(m.created_at)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="border-t border-slate-700/70 bg-slate-900/90 backdrop-blur px-3 py-2 flex items-center gap-2">
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFilePick} className="hidden" data-testid="doubt-file-input" />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || recording || disabled}
          className="p-2 rounded-full hover:bg-slate-700/60 disabled:opacity-40"
          data-testid="doubt-attach-btn"
          title="Send image"
        >
          {uploading ? <Loader2 className="animate-spin" size={20} /> : <ImgIcon size={20} />}
        </button>
        {!hideVoice && (
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={uploading || disabled}
            className={`p-2 rounded-full hover:bg-slate-700/60 disabled:opacity-40 ${recording ? 'bg-red-600 text-white animate-pulse' : ''}`}
            data-testid="doubt-mic-btn"
            title={recording ? 'Stop recording' : 'Record voice note'}
          >
            {recording ? <Square size={20} /> : <Mic size={20} />}
          </button>
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendText(); }
          }}
          rows={1}
          placeholder={disabled ? 'Chat is read-only' : (recording ? 'Recording…' : 'Type a message')}
          disabled={recording || disabled}
          className="flex-1 resize-none max-h-32 bg-slate-800/70 border border-slate-700/70 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/60 disabled:opacity-60"
          data-testid="doubt-msg-input"
        />
        <button
          type="button"
          onClick={handleSendText}
          disabled={sending || !text.trim() || recording || disabled}
          className="p-2 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
          data-testid="doubt-send-btn"
        >
          {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
        </button>
      </div>

      {/* Image preview modal */}
      {previewImg && (
        <div onClick={() => setPreviewImg(null)} className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <button className="absolute top-4 right-4 text-white" onClick={() => setPreviewImg(null)}>
            <X size={28} />
          </button>
          <img src={previewImg} alt="preview" className="max-w-[95vw] max-h-[90vh] object-contain rounded" />
        </div>
      )}
    </div>
  );
}
